import React, { useRef, useState } from "react";

// Swipe a row right to fire its one action (Today's Reply / Nudge).
//
// The button is still there and still does the same thing — this is a second
// way to reach it with the thumb that's already holding the phone, not a hidden
// one. Nothing is destructive behind the swipe and nothing is irreversible: it
// opens the draft, which is the same as tapping.
//
// `touch-action: pan-y` on the moving body is what makes this behave: the
// browser keeps vertical scrolling for itself and hands us the horizontal
// gesture, so we never have to preventDefault (React's touch listeners are
// passive and couldn't anyway).
const OPEN_AT = 64; // px of travel that counts as "yes"
const MAX = 104; // how far the row can be dragged
const SLOP = 10; // px before we decide the gesture is horizontal

export default function SwipeAct({ label, onAct, children }) {
  const [dx, setDx] = useState(0);
  const [sliding, setSliding] = useState(false);
  const start = useRef(null);
  const axis = useRef(null); // null until decided, then "x" | "y"
  // How far the row has travelled, kept alongside the state that renders it:
  // touchend can land in the same frame as the last touchmove, and reading the
  // render closure there would see a stale 0 and swallow the gesture.
  const travel = useRef(0);
  // A completed swipe is usually followed by a synthetic click. The row under us
  // navigates on click, so a swipe would open the draft AND leave the page.
  const swallowClick = useRef(false);

  function onTouchStart(e) {
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
    axis.current = null;
    travel.current = 0;
    swallowClick.current = false;
    setSliding(false);
  }

  function onTouchMove(e) {
    if (!start.current) return;
    const t = e.touches[0];
    const dX = t.clientX - start.current.x;
    const dY = t.clientY - start.current.y;
    if (!axis.current) {
      if (Math.abs(dX) < SLOP && Math.abs(dY) < SLOP) return;
      axis.current = Math.abs(dX) > Math.abs(dY) ? "x" : "y";
    }
    // Right only. A left drag would be a second, unlabelled action, and the one
    // that lands under a right-handed thumb by accident is the one that must
    // not do anything.
    if (axis.current === "x") {
      travel.current = Math.max(0, Math.min(dX, MAX));
      setDx(travel.current);
    }
  }

  function onTouchEnd() {
    const fired = travel.current >= OPEN_AT;
    start.current = null;
    axis.current = null;
    travel.current = 0;
    swallowClick.current = fired;
    setSliding(true);
    setDx(0);
    if (fired) onAct();
  }

  // A cancelled touch is the system taking the gesture away (a call, an edge
  // swipe) — it is not a decision, so it commits nothing.
  function onTouchCancel() {
    start.current = null;
    axis.current = null;
    travel.current = 0;
    swallowClick.current = false;
    setSliding(true);
    setDx(0);
  }

  const armed = dx >= OPEN_AT;

  return (
    <div className="crm-swipe">
      <div className={`crm-swipe__act${armed ? " is-armed" : ""}`} aria-hidden="true">
        <span>{label}</span>
      </div>
      <div
        className={`crm-swipe__body${sliding ? " is-sliding" : ""}`}
        style={dx ? { transform: `translateX(${dx}px)` } : undefined}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onClickCapture={(e) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.stopPropagation();
          e.preventDefault();
        }}
        onTransitionEnd={() => setSliding(false)}
      >
        {children}
      </div>
    </div>
  );
}
